import { useContext, type JSX } from 'react';
import AnalysInitQuery from './AnalysInitQuery.gql';
import {
  AnalysInitQuery as IData,
  AnalysInitQueryVariables as IVariables,
  AnalysInitQuery_logo,
} from './_QueryTypes/AnalysInitQuery';
import { Query } from '@apollo/client/react/components';
import { KodplanContext } from '../../../../../../../Components/KodplanManager/Context/KodplanContext';
import { getGbsRoles } from '../../../Analysutdelning/Components/AnalysUtdelningTable/Utils/AnalysUtdelningTableUtils';
import { IGBSRole } from '../../../Analysutdelning/Queries/AnalysutdelningQuery/AnalysutdelningQuery';
import mergeSort, {
  stringCompareNumeric,
} from '../../../../../../../Utils/mergeSort';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import {
  IUserInfo,
  getUserInfo,
} from '../../Utils/ekonomiskAnalysInitQueryUtils';
import { paddedEkonomiskAnalysRutinId } from '../../Components/SideMenu/EkonomiskAnalysSelect/Components/FavoriteAnalysDialog/Queries/ChangeStartAnalysMutation/ChangeStartAnalysMutation';
import Logger from '../../../../../../../Utils/Logger';

export interface IAnalys {
  id: string; // Analysens id
  idkod?: string; // Analysens idkod om standard rapport
  name: string; // Analysens namn
  baseSelection: IBaseSelection;
  favoritLopnr: number | null;
  utdelatLopnr: number | null;
  kategori: string;
  kategori_ID: number | undefined;
  defaultFromYear?: number;
  defaultFromPeriod?: number;
  obligatorisk: boolean;
  obligatoriskRoller: IGBSRole[];
  detaljanalys: boolean;
  ai_prompt: string | null;
}

export interface ISelection {
  id: string; // Kodbehörighet id ()
  name: string; // Kodbehörighetens namn
  anvandsFor: string | null;
  kombinerad: boolean;
  analysList: IAnalys[]; // Lista med Analyser
}

export interface IBaseSelection {
  id?: string;
  name?: string;
}

interface IProps {
  codeSystemId: string;
  onlyFavorites?: boolean;
  children: (childProps: IChildProps) => JSX.Element | null;
}

interface IChildProps {
  availableAnalyser?: ISelection[];
  listQueryLoading: boolean;
  hasListQueryData?: boolean;
  userInfo?: IUserInfo;
  ekLogo?: AnalysInitQuery_logo;
}

const isObligatorisk = (
  myGbsRoles: IGBSRole[],
  obligatoriskGasRoles: string
) => {
  const obligatoriskForRoleCodes = obligatoriskGasRoles
    .split(',')
    .map(role => role.trim());

  return obligatoriskForRoleCodes.some(obligatoriskForRoleCode =>
    myGbsRoles.some(myGbsRole => myGbsRole.code === obligatoriskForRoleCode)
  );
};

const EkonomiskAnalysInitQuery = (props: IProps) => {
  const { onlyFavorites = false, codeSystemId } = props;

  const kodplanContext = useContext(KodplanContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const skip = !activeDatabaseContext.activeDatabase;

  return (
    <Query<IData, IVariables>
      query={AnalysInitQuery}
      fetchPolicy="cache-and-network"
      variables={{
        codeSystemId: codeSystemId,
        onlyFavorites: onlyFavorites,
        dbName: activeDatabaseContext.activeDatabase ?? '',
        section: [`Forms/${paddedEkonomiskAnalysRutinId}`],
        subsystemCode: 'EK',
      }}
      skip={skip}
    >
      {({ loading, error, data }) => {
        let selections: ISelection[] = [];

        let info: IUserInfo = {
          useCurrentDateAsStartDate: false,
          delsystemAnsvarigEkonomi: false,
          updateStartanalysAtLogin: true,
          isUtdelningBehorig: false,
          canHandleBfo: {
            result: false,
            message: '',
          },
        };

        if (error) {
          //TODO:EK Kasta ett fel här istället för att evighetsladda
          Logger.error(error);
          throw new Error(error.message);
        }

        if (!error && data) {
          if (data.ekkodbehmedanalyser.kodbehorigheter) {
            const codePrivileges = data.ekkodbehmedanalyser.kodbehorigheter;

            selections = codePrivileges.map((codePrivilege): ISelection => {
              const utdeladeAnalyser = codePrivilege.utdeladeanalyser;

              return {
                id: codePrivilege.kodbeh_id,
                name: codePrivilege.name,
                anvandsFor: codePrivilege.anvands_for,
                kombinerad: codePrivilege.kombinerad ?? false,
                analysList: !utdeladeAnalyser
                  ? []
                  : utdeladeAnalyser.map(
                      (analys): IAnalys => ({
                        id: analys.analys_id,
                        idkod: analys.idkod ? analys.idkod : undefined,
                        name: analys.name,
                        favoritLopnr: analys.favorit_lopnr,
                        utdelatLopnr: analys.utdelning_lopnr,
                        kategori: analys.Kategori || '',
                        kategori_ID: analys.kategori_id || undefined,
                        obligatorisk: kodplanContext.activeKodplan?.gas
                          ? isObligatorisk(
                              data?.me?.[0]?.roles ?? [],
                              analys.obligatorisk_gas ?? ''
                            )
                          : analys.obligatorisk_ej_gas,
                        obligatoriskRoller: analys.obligatorisk_gas
                          ? getGbsRoles(
                              analys.obligatorisk_gas,
                              data.gbsroles ?? []
                            )
                          : [],
                        baseSelection: {
                          id:
                            analys.grundurval_id != null
                              ? analys.grundurval_id
                              : undefined,
                          name:
                            analys.grundurval_namn != null
                              ? analys.grundurval_namn
                              : undefined,
                        },
                        detaljanalys: analys.detaljanalys ?? false,
                        ai_prompt: analys.ai_prompt ?? null,
                      })
                    ),
              };
            });

            selections = mergeSort(selections, (a, b) => {
              if (a.kombinerad && !b.kombinerad) {
                return -1;
              } else if (b.kombinerad && !a.kombinerad) {
                return 1;
              } else {
                return stringCompareNumeric(a.name, b.name);
              }
            });
          }

          if (data.me) {
            info = getUserInfo(
              data,
              data.me,
              activeDatabaseContext,
              selections
            );
          }

          if (data.ekanalyssenasteklarrapporteradeperiod) {
            info.lastReportedPeriod = {
              year: data.ekanalyssenasteklarrapporteradeperiod.bokfar,
              month: data.ekanalyssenasteklarrapporteradeperiod.period,
            };
          }

          return props.children({
            availableAnalyser: selections,
            listQueryLoading: false,
            hasListQueryData: data !== undefined && !error,
            userInfo: info,
            ekLogo: data.logo,
          });
        }
        return props.children({
          listQueryLoading: true,
        });
      }}
    </Query>
  );
};

export default EkonomiskAnalysInitQuery;
